import { memo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  Chip
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import EditIcon from "@mui/icons-material/Edit";

/**
 * Componente de cartão de marca.
 * Exibe informações da marca com status e botão de edição (apenas para admin).
 * 
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {string|number} props.id - ID único da marca
 * @param {string} props.name - Nome da marca
 * @param {boolean} props.enable - Status da marca (ativa/inativa)
 * @param {string} [props.image_url] - URL da imagem da marca
 * @param {boolean} [props.isAdmin=false] - Se usuário é admin
 * @param {Function} [props.onClick] - Callback ao clicar no card
 * @param {Function} [props.onEdit] - Callback para editar marca (apenas admin)
 * @returns {JSX.Element} Cartão da marca
 */
function BrandCard({ id, name, enable, image_url, isAdmin = false, onClick, onEdit }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleCardClick = () => {
    if (onClick) {
      onClick({ id, name, enable, image_url });
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit({ id, name, enable, image_url });
    }
  };

  return (
    <Card 
      onClick={handleCardClick}
      sx={{ 
        position: "relative", 
        width: { xs: '98%', sm: '100%' },
        minWidth: { xs: 0, sm: 280 },
        maxWidth: '100%',
        height: 350, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 350,
        maxHeight: 350,
        flex: 1,
        opacity: enable ? 1 : 0.6,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        }
      }}
    >
      <Box sx={{ 
        height: 180, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.50',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {image_url ? (
          <Box
            component="img"
            src={image_url}
            alt={name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <StoreIcon 
          sx={{ 
            fontSize: 80,
            color: 'primary.main',
            display: image_url ? 'none' : 'flex'
          }} 
        />
        {isAdmin && (
          <Chip
            label={enable ? 'Ativa' : 'Inativa'}
            color={enable ? 'success' : 'default'}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          />
        )}
      </Box>
      
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        height: 170,
        p: 2
      }}>
        <Typography variant="h6" component="h3" sx={{ 
          mb: 1, 
          fontSize: '1rem',
          height: { xs: 'auto', md: '2.5rem' },
          overflow: 'hidden',
          display: { xs: 'block', md: '-webkit-box' },
          WebkitLineClamp: { xs: 'none', md: 2 },
          WebkitBoxOrient: { xs: 'initial', md: 'vertical' },
          lineHeight: 1.25,
          textOverflow: { xs: 'ellipsis', md: 'initial' },
          whiteSpace: { xs: 'nowrap', md: 'initial' },
          textAlign: 'center'
        }}>
          {name}
        </Typography>
        
        {isAdmin && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 'auto'
            }}
          >
            <Button 
              variant="contained" 
              size="small"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              sx={{ width: '100%' }}
            >
              {isMobile ? 'Editar' : 'Editar Marca'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default memo(BrandCard);
